/**
 * ccloop.mjs — the agent loop you already understand, measured from a real session.
 *
 * FOUNDER, 2026-08-15: "add a module in end for mastering claudecode zero to hero" ·
 * "ensure course is world class and not paper and texty".
 *
 * ── CHAPTER 1 HAS NOTHING NEW TO TEACH, AND THAT IS THE POINT ─────────────────────────────────
 * CodeAI101 chapter 4: "You set goal → AI plans → AI calls tool → result → AI plans → AI calls tool
 * → ... → AI reports back." Chapter 3: a tool call is a function the AI decides to invoke, and a
 * harness runs it for real. A learner arriving here already knows the shape. Claude Code is that
 * shape, on their own machine, against their own files — so the honest chapter 1 does not explain a
 * new idea, it shows the old one running.
 *
 * ── EVERY NUMBER HERE CAME OUT OF A REAL TRANSCRIPT ───────────────────────────────────────────
 * Counted from the session that built the academy's CodeAI101 labs — a JSONL file on this machine,
 * one record per event. Not an illustration, not a typical figure: this session, counted.
 *
 * ── AND IT INDEPENDENTLY CONFIRMS HIS OWN FIGURE ──────────────────────────────────────────────
 * CodeAI101 §5 states "Claude Code defaults to ~25-50 tool calls per task". Measured here: 576 tool
 * calls across 16 prompts, which is 36.0 per prompt — inside his range, from data he never saw when
 * he wrote it. That is worth showing a learner precisely because nobody arranged it.
 *
 * ── WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────────────────────────
 * Tool NAMES and COUNTS only. No arguments, no commands, no file paths, no output. A transcript
 * holds whatever the work touched — source, configuration, occasionally a secret — and shipping any
 * of it into a public browser bundle to illustrate a lesson would be indefensible. The shape of the
 * loop is the lesson; the contents are not needed for it and are not here.
 */

export const SOURCE_SESSION = 'the Claude Code session that built the CodeAI101 labs, counted from its transcript on 2026-08-15';
export const SOURCE_CH4 = 'CodeAI101 module 04 §1 "The two shapes of AI use — one-shot vs looping"';
export const SOURCE_CH4_LIMIT = 'CodeAI101 module 04 §5 "Goals, attempts, limits, termination — keeping agents bounded"';
export const SOURCE_CH3 = 'CodeAI101 module 03 "Tool Use — Letting AI Act on the World"';

/** His loop, quoted, so the learner sees they already have this. */
export const HIS_LOOP = 'You set goal → AI plans → AI calls tool → result → AI plans → AI calls tool → ... → AI reports back';
export const HIS_LIMIT = 'Usually configurable; defaults exist (Claude Code defaults to ~25-50 tool calls per task; Codex similar).';

/**
 * Counted from the transcript. The gate re-counts them from the same file.
 *
 * ── THESE ARE FLOORS, NOT TOTALS, AND THAT IS THE HONEST SHAPE ────────────────────────────────
 * The first version of this gate asserted the counts EQUALLY and went red immediately — because the
 * transcript is live. The session that built these labs was still being written while its own
 * numbers were being read, so every figure was already stale by the time it was recorded.
 *
 * That is not a flaw to paper over; it is chapter 1's lesson arriving early. You cannot take a
 * still measurement of a loop from inside it. The file is append-only, so what a count gives you is
 * a FLOOR — at least this many, as at that moment — and the gate asserts the live figure has only
 * ever grown. A number presented as a total when it could only ever be a minimum would be the
 * course overstating its own evidence in its first chapter.
 */
export const MEASURED = Object.freeze({
  counted_on: '2026-08-15',
  are_floors: true,
  why_floors: 'Counted from a session that was still running. The transcript only ever grows, so each figure is "at least this many, as at 2026-08-15" — never a final total.',
  records: 2409,
  prompts: 16,
  tool_calls: 576,
  /* Tool kinds arrive LATE. Three appeared when the founder asked us to look at how another
     organisation teaches this; `Read` appeared later still, on 2026-08-16, when the work turned
     from building files to reading them. The gate caught each one the same way — it re-counts the
     transcript and notices kinds the lab does not name. Worth keeping visible rather than folding
     away: a session's tool profile is not fixed at the start, it is a record of what the work
     turned out to need.

     `Read`'s figure is from the LATER count, and is marked so. Mixing a fresh measurement into a
     dated snapshot without saying which is which is how a snapshot quietly becomes a fiction. */
  by_tool: Object.freeze([
    { name: 'Bash', n: 510, what: 'run a command — the shell, which is most of what building software is' },
    { name: 'Write', n: 62, what: 'create a file' },
    { name: 'Edit', n: 3, what: 'change part of an existing file' },
    { name: 'Read', n: 5, as_at: '2026-08-16', what: 'read a file directly — absent from the first count entirely, because that stretch of work read files by shelling out instead' },
    { name: 'AskUserQuestion', n: 1, what: 'stop and ask the human to decide' },
    { name: 'ToolSearch', n: 1, what: 'load a tool it did not start the session holding' },
    { name: 'WebSearch', n: 1, what: 'search the web — reaching outside this machine for the first time' },
    { name: 'WebFetch', n: 1, what: 'read a specific page' },
    /* ── THREE TOOLS THE GATE FOUND BEFORE ANYONE THOUGHT TO LOOK (2026-08-18) ──────────────────
       The session reached into a mailbox — searching threads, opening one, reading a message — to
       find four chapters a contributor had re-sent. Nothing in the lab named those tools, and the
       gate went red on its next run without being asked to.
       That is the chapter's own argument arriving in the chapter's own machinery: the session doing
       the counting is the session being counted, and a recorded list of what a session used decays
       the moment the session does something new. Added as FLOORS like the rest. */
    { name: 'mcp__claude_ai_Gmail__search_threads', n: 3, as_at: '2026-08-18', what: 'search a mailbox — the first time this session reached into mail rather than the disk' },
    { name: 'mcp__claude_ai_Gmail__get_thread', n: 1, as_at: '2026-08-18', what: 'open one conversation in full' },
    { name: 'mcp__claude_ai_Gmail__get_message', n: 2, as_at: '2026-08-18', what: 'read a single message, and its attachment metadata' },
    /* Two more, hours later, and the gate caught these the same way — the session published its
       first pages. THIS IS THE THIRD TIME IN ONE DAY that a list of what the session used went
       stale while the session was still running. Three corrections is no longer an anecdote about
       one oversight; it is the measurement the chapter is making. A recorded inventory of a live
       thing is wrong by the time it is read, and the only reason anyone knows is that something
       re-asks. */
    { name: 'Skill', n: 1, as_at: '2026-08-18', what: 'load a packaged set of instructions before doing the work it governs' },
    { name: 'Artifact', n: 2, as_at: '2026-08-18', what: 'publish a page — the first time this session produced something for someone else to open' },
  ]),
});

/**
 * ── THE SAME SESSION, COUNTED AGAIN A DAY LATER ───────────────────────────────────────────────
 *
 * The comment below used to promise that "if the numbers are ever re-counted and fall outside, the
 * lab says so instead of continuing to claim a tidy confirmation". Nothing could ever trigger that,
 * because the ratio was computed from a frozen snapshot — so the lab went on announcing a tidy
 * confirmation it could no longer have checked.
 *
 * It has now been re-counted, and it does fall outside. Both counts are kept.
 */
export const REMEASURED = Object.freeze({
  counted_on: '2026-08-16',
  records: 5219,
  prompts: 154,
  tool_calls: 1011,
  /* ── AND THIS ONE SUMS ──────────────────────────────────────────────────────────────────
     The first count's per-tool figures add to 579 against a stated total of 576, because the
     total was recorded before four tool kinds had appeared and nothing re-took it. Parts that do
     not add to their whole is a defect however honestly each part was counted, and a table
     printed beside a total invites exactly that addition. This snapshot was taken in one pass. */
  by_tool: Object.freeze([
    { name: 'Bash', n: 913 }, { name: 'Write', n: 75 }, { name: 'Edit', n: 12 },
    { name: 'Read', n: 5 }, { name: 'WebSearch', n: 2 }, { name: 'WebFetch', n: 2 },
    { name: 'AskUserQuestion', n: 1 }, { name: 'ToolSearch', n: 1 },
  ]),
});
export const PARTS_SUM = (m) => (m.by_tool || []).reduce((a, t) => a + t.n, 0);
export const FIRST_COUNT_DOES_NOT_SUM = 'The first count\'s per-tool figures add to 579 against a recorded total of 576 — the total was taken before four tool kinds had appeared, and nothing re-took it. Both were honestly counted; they are simply not the same moment. The later snapshot was taken in one pass and does add up.';

/**
 * ── A RATIO OF TWO FLOORS IS NOT A FLOOR ──────────────────────────────────────────────────────
 *
 * Every count above is a minimum, and the lab said so. The rate derived from two of them was then
 * displayed under the same "at least this many" heading — which was wrong, and provably so: the
 * live rate is now well BELOW the figure presented as a floor. Two numbers that can only grow do
 * not give a quotient that can only grow.
 *
 * The rate is therefore a ratio of one PAIRED snapshot, dated, and never called a minimum.
 */
export const A_RATIO_IS_NOT_A_FLOOR = 'Each count is a floor — the transcript only grows. The rate is not. It is a quotient of two floors taken at the same moment, and it moves in both directions: re-counted a day later, the same session gives 6.6.';

export const perPrompt = () => Math.round((MEASURED.tool_calls / MEASURED.prompts) * 10) / 10;
export const perPromptNow = () => Math.round((REMEASURED.tool_calls / REMEASURED.prompts) * 10) / 10;
/** Live figures, when a caller can supply them — the lab cannot, a gate can. */
export const rateOf = (calls, prompts) => Math.round((calls / prompts) * 10) / 10;

/**
 * ── AND THE DENOMINATOR IS A CHOICE, WHICH THE LAB MUST NAME ──────────────────────────────────
 *
 * "Prompts" is not a labelled thing in a transcript. This counts records of type `last-prompt`.
 * At least one other defensible rule exists — user records carrying no tool result — and on the
 * same file it gives 88 rather than 154. Neither is wrong; they are different questions. A rate
 * whose denominator is a choice, presented without naming the choice, is a number wearing more
 * authority than it earned.
 */
export const THE_DENOMINATOR = 'Counted as records of type `last-prompt`. A second defensible rule — user records carrying no tool result — gives 88 rather than 154 on the same file. The denominator is a choice, so the lab names which one it made.';

/**
 * HIS RANGE, AND WHETHER THE REAL SESSION LANDED IN IT.
 *
 * Computed rather than asserted, so that when the numbers were re-counted and fell outside, the lab
 * said so instead of continuing to claim a tidy confirmation. That is now what happened.
 */
export const HIS_RANGE = Object.freeze([25, 50]);
export const inHisRange = (r) => r >= HIS_RANGE[0] && r <= HIS_RANGE[1];
export const landsInHisRange = () => inHisRange(perPrompt());
export const landsNow = () => inHisRange(perPromptNow());

/**
 * WHAT THE TWO COUNTS ACTUALLY TEACH, which is more than the confirmation did.
 *
 * The first count came from a stretch of long autonomous building — few prompts, many tool calls.
 * The second spans a stretch of short exchanges. The tool calls roughly doubled while the prompts
 * went up nearly tenfold, so the rate fell by a factor of five. Nothing about the tool changed.
 */
export const WHY_IT_MOVED = 'The rate is not a property of the tool. It is a property of how the work is being done: long autonomous stretches give a high number, short exchanges a low one. Between the two counts the tool calls roughly doubled while the prompts rose nearly tenfold, and nothing about the tool changed at all.';
export const THE_CONFIRMATION_DID_NOT_SURVIVE = 'The first count landed inside the range quoted from CodeAI101, and this lab announced it. The second does not. The honest reading is not that one count is wrong — both were taken the same way from the same file — but that a single ratio was never evidence for a general figure, and presenting it as a confirmation was the mistake.';

/**
 * A REAL THIRTY-STEP WINDOW, taken consecutively from the middle of the session.
 *
 * Actor labels and tool names only. 'you' is the human; note where it appears — a person stepping
 * into a loop that was running without them, which is CodeAI101 chapter 4's "you can interrupt at
 * any time" happening in real work rather than described.
 */
export const WINDOW = Object.freeze([
  'Bash', 'Bash', 'assistant', 'Bash', 'assistant', 'Bash', 'Bash', 'Bash', 'Bash', 'assistant',
  'Bash', 'Bash', 'Bash', 'assistant', 'Bash', 'assistant', 'Bash', 'assistant', 'Bash', 'Bash',
  'assistant', 'you', 'assistant', 'Bash', 'Bash', 'assistant', 'Bash', 'Bash', 'assistant', 'Bash',
]);
export const WINDOW_IS_REAL = 'Thirty consecutive steps from the real transcript. Only the actor and the tool name are shown — no commands, no arguments, no output.';

/** What each step in the window IS, in the vocabulary the learner already has. */
export const roleOf = (step) =>
  step === 'you' ? { who: 'you', kind: 'human', says: 'A person interrupts. The loop was running without them until this moment.' }
    : step === 'assistant' ? { who: 'the AI', kind: 'plan', says: 'It reasons or reports — no tool, no action on your machine.' }
      : { who: 'the harness', kind: 'tool', says: `A ${step} call. The AI decided to make it; the harness actually ran it.` };

/** Where the human sat on chapter 4's dial during this session — evidenced, not claimed. */
export const THE_DIAL_POSITION = Object.freeze({
  evidence: `${MEASURED.tool_calls} tool calls across ${MEASURED.prompts} prompts, with one AskUserQuestion in the whole session`,
  reading: 'Auto-run with monitoring — CodeAI101 chapter 4\'s middle position. The loop ran without per-step approval and a human was watching and interrupting, which is exactly what the window above shows.',
  is_a_reading: 'That is an inference from the counts, not something the transcript declares. One explicit question in 576 calls is evidence of a loop that was trusted to run, not proof of which setting was chosen.',
});

export const THE_POINT = 'Nothing here is a new idea. It is CodeAI101 chapter 4\'s loop and chapter 3\'s tool calls, on a real machine, against real files — and the numbers are what that looks like over one working session.';
